import { isValidElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import xss from 'xss';
import type { ReactElement, ReactNode } from 'react';
import { Accordion, BasicCollapsible, Column, GridContainer, Row } from '@vp/swan';

export type FAQ = {
    question: ReactElement | string;
    answer: ReactNode;
    id?: string;
};

interface FAQAccordionProps {
    faqData: FAQ[];
}

const FAQAccordion = ({ faqData }: FAQAccordionProps) => {
    const processFaqInputForSchema = (faqInput: ReactNode): string => {
        if (typeof faqInput === 'string') {
            return faqInput;
        }
        if (isValidElement(faqInput)) {
            // Convert react node to static html string
            const rawHtml = renderToStaticMarkup(faqInput);

            // Remove html tags not needed in schema
            return xss(rawHtml, {
                whiteList: {
                    a: ['href'],
                    b: [],
                    ul: [],
                    ol: [],
                    li: [],
                },
                stripIgnoreTag: true,
            });
        }
        return '';
    };

    const generateFAQSchema = (faqData: FAQ[]) => ({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqData.map((faqData) => {
            return {
                '@type': 'Question',
                name: processFaqInputForSchema(faqData.question),
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: processFaqInputForSchema(faqData.answer),
                },
            };
        }),
    });

    return (
        <>
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(generateFAQSchema(faqData)) }}
            />
            <Accordion>
                {faqData.map(({ question, answer, id }, index) => (
                    <BasicCollapsible
                        key={`faq-${id}` || `faq-${index}`}
                        collapsibleId={`faq-${id}` || `faq-${index}`}
                        heading={question}
                        headingLevel="h3"
                    >
                        <GridContainer>
                            <Row>
                                <Column span={9} spanXs={12} spanSm={12}>
                                    {answer}
                                </Column>
                            </Row>
                        </GridContainer>
                    </BasicCollapsible>
                ))}
            </Accordion>
        </>
    );
};

export default FAQAccordion;
